import { useEffect } from "react";
import { Link } from "react-router-dom";
import { Shield, Lock, Eye, FileText, Users, Globe, Mail, Phone, Building } from "lucide-react";

export default function PrivacyPolicy() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const lastUpdated = "March 2, 2026";

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className="bg-[#121212] text-white py-6 px-6 lg:px-12">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-10 h-10 bg-[#BFFF00] rounded-xl flex items-center justify-center">
              <span className="text-[#121212] font-bold text-xl">F</span>
            </div>
            <span className="text-2xl font-bold font-['Manrope']">FinAI</span>
          </Link>
          <nav className="flex items-center gap-6 text-sm">
            <Link to="/terms" className="hover:text-[#BFFF00]">Terms</Link>
            <Link to="/security" className="hover:text-[#BFFF00]">Security</Link>
            <Link to="/login" className="bg-[#BFFF00] text-[#121212] px-4 py-2 rounded-full font-medium hover:bg-[#a8e600]">Login</Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-b from-[#F7FFE5] to-white py-16 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <div className="w-16 h-16 bg-[#BFFF00] rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8 text-[#121212]" />
          </div>
          <h1 className="text-4xl lg:text-5xl font-bold text-[#121212] font-['Manrope'] mb-4">Privacy Policy</h1>
          <p className="text-gray-600 text-lg">Last Updated: {lastUpdated}</p>
        </div>
      </div>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <div className="prose prose-lg max-w-none">
          
          {/* Introduction */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <FileText className="w-6 h-6 text-[#BFFF00]" />
              Introduction
            </h2>
            <p className="text-gray-700 leading-relaxed">
              Revurge Corporation ("Revurge," "we," "us," or "our"), operating FinAI by Revurge, is committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our bookkeeping, accounting, and financial services platform.
            </p>
            <p className="text-gray-700 leading-relaxed mt-4">
              This policy is designed to comply with the <strong>Gramm-Leach-Bliley Act (GLBA)</strong>, the <strong>California Consumer Privacy Act (CCPA)</strong>, and other applicable privacy regulations. By using our services, you consent to the practices described in this policy.
            </p>
            <div className="bg-[#F7FFE5] border border-[#BFFF00]/30 rounded-xl p-4 mt-4">
              <p className="text-sm text-gray-700">
                <strong>Company Information:</strong><br />
                Revurge Corporation<br />
                8 The Green, Dover, DE 19901<br />
                Email: info@revurge.com<br />
                Compliance: compliance@revurge.com
              </p>
            </div>
          </section>

          {/* Information We Collect */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Eye className="w-6 h-6 text-[#BFFF00]" />
              Information We Collect
            </h2>
            
            <h3 className="text-xl font-semibold text-[#121212] mt-6 mb-3">Personal Information</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Identity Information:</strong> Full name, date of birth, government-issued ID numbers</li>
              <li><strong>Contact Information:</strong> Email address, phone number, mailing address</li>
              <li><strong>Business Information:</strong> Company name, Employer Identification Number (EIN), business address, industry type</li>
              <li><strong>Authentication Credentials:</strong> Unique user IDs, passwords (encrypted), multi-factor authentication data</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#121212] mt-6 mb-3">Financial Information</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Bank Account Data:</strong> Account numbers, routing numbers, transaction history (via Plaid integration)</li>
              <li><strong>Payment Information:</strong> Credit/debit card details (processed via PCI-DSS compliant Stripe)</li>
              <li><strong>Tax Information:</strong> Tax returns, W-2s, 1099s, and other tax documents</li>
              <li><strong>Invoice Data:</strong> Client billing information, payment histories</li>
            </ul>

            <h3 className="text-xl font-semibold text-[#121212] mt-6 mb-3">Technical Information</h3>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Device Data:</strong> IP address, browser type, operating system, device identifiers</li>
              <li><strong>Usage Data:</strong> Pages visited, features used, time spent on platform</li>
              <li><strong>Cookies and Tracking:</strong> Session cookies, analytics cookies (see our <Link to="/cookies" className="text-blue-600 hover:underline">Cookie Policy</Link>)</li>
            </ul>
          </section>

          {/* How We Use Your Information */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Users className="w-6 h-6 text-[#BFFF00]" />
              How We Use Your Information
            </h2>
            <p className="text-gray-700 mb-4">We use your information for the following purposes:</p>
            
            <div className="grid md:grid-cols-2 gap-4">
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-[#121212] mb-2">Service Delivery</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Processing financial transactions</li>
                  <li>• Generating invoices and reports</li>
                  <li>• Bank account synchronization</li>
                  <li>• Tax preparation and filing</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-[#121212] mb-2">Security & Compliance</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Identity verification (KYC)</li>
                  <li>• Fraud prevention</li>
                  <li>• Regulatory compliance</li>
                  <li>• Audit trail maintenance</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-[#121212] mb-2">AI-Powered Features</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Document analysis and categorization</li>
                  <li>• Expense classification</li>
                  <li>• Financial insights generation</li>
                  <li>• Anomaly detection</li>
                </ul>
              </div>
              <div className="bg-gray-50 rounded-xl p-4">
                <h4 className="font-semibold text-[#121212] mb-2">Communication</h4>
                <ul className="text-sm text-gray-600 space-y-1">
                  <li>• Service notifications</li>
                  <li>• Security alerts</li>
                  <li>• Product updates</li>
                  <li>• Customer support</li>
                </ul>
              </div>
            </div>
          </section>

          {/* GLBA Compliance */}
          <section className="mb-12 bg-blue-50 rounded-2xl p-6">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6 text-blue-600" />
              GLBA Safeguards Rule Compliance
            </h2>
            <p className="text-gray-700 mb-4">
              As a financial services provider, we comply with the Gramm-Leach-Bliley Act (GLBA) Safeguards Rule. Our information security program includes:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Designated Security Coordinator:</strong> Our Chief Compliance Officer (CCO) oversees our information security program</li>
              <li><strong>Risk Assessment:</strong> Regular identification and assessment of risks to customer information</li>
              <li><strong>Safeguards Implementation:</strong> Administrative, technical, and physical safeguards to protect data</li>
              <li><strong>Service Provider Oversight:</strong> Due diligence and contractual requirements for third-party vendors</li>
              <li><strong>Program Evaluation:</strong> Regular testing and monitoring of our security controls</li>
              <li><strong>Incident Response:</strong> Documented procedures for responding to security incidents</li>
            </ul>
          </section>

          {/* CCPA Rights */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Globe className="w-6 h-6 text-[#BFFF00]" />
              California Consumer Privacy Act (CCPA) Rights
            </h2>
            <p className="text-gray-700 mb-4">If you are a California resident, you have the following rights:</p>
            
            <div className="space-y-4">
              <div className="border-l-4 border-[#BFFF00] pl-4">
                <h4 className="font-semibold text-[#121212]">Right to Know</h4>
                <p className="text-sm text-gray-600">Request disclosure of the categories and specific pieces of personal information we've collected about you.</p>
              </div>
              <div className="border-l-4 border-[#BFFF00] pl-4">
                <h4 className="font-semibold text-[#121212]">Right to Delete</h4>
                <p className="text-sm text-gray-600">Request deletion of your personal information, subject to certain exceptions.</p>
              </div>
              <div className="border-l-4 border-[#BFFF00] pl-4">
                <h4 className="font-semibold text-[#121212]">Right to Opt-Out</h4>
                <p className="text-sm text-gray-600">Opt-out of the sale of your personal information (we do not sell personal information).</p>
              </div>
              <div className="border-l-4 border-[#BFFF00] pl-4">
                <h4 className="font-semibold text-[#121212]">Right to Non-Discrimination</h4>
                <p className="text-sm text-gray-600">Exercise your rights without discriminatory treatment.</p>
              </div>
            </div>
            
            <p className="text-gray-700 mt-4">
              To exercise these rights, contact us at <a href="mailto:compliance@revurge.com" className="text-blue-600 hover:underline">compliance@revurge.com</a> or call us at the number below.
            </p>
          </section>

          {/* Data Sharing */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4">Information Sharing and Disclosure</h2>
            <p className="text-gray-700 mb-4">We may share your information with:</p>
            
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li><strong>Service Providers:</strong> Third parties who perform services on our behalf (see <Link to="/third-party-providers" className="text-blue-600 hover:underline">Third-Party Service Provider Policy</Link>)</li>
              <li><strong>Financial Institutions:</strong> Banks and payment processors to complete transactions</li>
              <li><strong>Legal Requirements:</strong> When required by law, subpoena, or government request</li>
              <li><strong>Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
              <li><strong>With Your Consent:</strong> When you explicitly authorize sharing</li>
            </ul>
            
            <div className="bg-red-50 border border-red-200 rounded-xl p-4 mt-4">
              <p className="text-sm text-red-800">
                <strong>We do NOT:</strong> Sell your personal information to third parties, share your data for marketing purposes without consent, or provide access to your financial data to unauthorized parties.
              </p>
            </div>
          </section>

          {/* Data Retention */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4">Data Retention</h2>
            <p className="text-gray-700 mb-4">We retain your information for as long as necessary to:</p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2">
              <li>Provide our services to you</li>
              <li>Comply with legal obligations (typically 7 years for financial records)</li>
              <li>Resolve disputes and enforce agreements</li>
              <li>Meet regulatory requirements</li>
            </ul>
            <p className="text-gray-700 mt-4">
              When data is no longer needed, it is securely deleted or anonymized in accordance with our data destruction policies.
            </p>
          </section>

          {/* Security */}
          <section className="mb-12 bg-[#121212] text-white rounded-2xl p-6">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6 text-[#BFFF00]" />
              Security Measures
            </h2>
            <p className="mb-4 text-gray-300">
              We implement industry-leading security measures to protect your data:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-[#BFFF00] mb-2">Technical Safeguards</h4>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• AES-256 encryption at rest</li>
                  <li>• TLS 1.3 encryption in transit</li>
                  <li>• Multi-factor authentication (MFA)</li>
                  <li>• Regular penetration testing</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-[#BFFF00] mb-2">Compliance Standards</h4>
                <ul className="text-sm text-gray-300 space-y-1">
                  <li>• SOC 2 Type II aligned</li>
                  <li>• PCI DSS compliant payment processing</li>
                  <li>• NIST Cybersecurity Framework</li>
                  <li>• ISO 27001 aligned practices</li>
                </ul>
              </div>
            </div>
            <p className="mt-4 text-sm text-gray-400">
              For more details, see our <Link to="/security" className="text-[#BFFF00] hover:underline">Security & Compliance page</Link>.
            </p>
          </section>

          {/* Contact */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4 flex items-center gap-2">
              <Mail className="w-6 h-6 text-[#BFFF00]" />
              Contact Us
            </h2>
            <p className="text-gray-700 mb-4">
              If you have questions about this Privacy Policy or wish to exercise your rights, please contact our Data Protection Officer:
            </p>
            <div className="bg-gray-50 rounded-xl p-6">
              <div className="flex items-start gap-4">
                <Building className="w-6 h-6 text-[#121212] mt-1" />
                <div>
                  <p className="font-semibold text-[#121212]">Revurge Corporation</p>
                  <p className="text-gray-600">Attn: Data Protection Officer</p>
                  <p className="text-gray-600">8 The Green</p>
                  <p className="text-gray-600">Dover, DE 19901</p>
                  <p className="text-gray-600 mt-2">
                    <strong>General:</strong> <a href="mailto:info@revurge.com" className="text-blue-600 hover:underline">info@revurge.com</a>
                  </p>
                  <p className="text-gray-600">
                    <strong>Compliance:</strong> <a href="mailto:compliance@revurge.com" className="text-blue-600 hover:underline">compliance@revurge.com</a>
                  </p>
                </div>
              </div>
            </div>
          </section>

          {/* Changes to Policy */}
          <section className="mb-12">
            <h2 className="text-2xl font-bold text-[#121212] mb-4">Changes to This Policy</h2>
            <p className="text-gray-700">
              We may update this Privacy Policy from time to time. We will notify you of any material changes by posting the new policy on this page and updating the "Last Updated" date. For significant changes, we will provide additional notice via email or in-app notification.
            </p>
          </section>

        </div>
      </main>

      {/* Footer */}
      <footer className="bg-[#121212] text-white py-12 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="flex flex-wrap justify-center gap-6 text-sm text-gray-400">
            <Link to="/privacy" className="hover:text-[#BFFF00]">Privacy Policy</Link>
            <Link to="/terms" className="hover:text-[#BFFF00]">Terms of Service</Link>
            <Link to="/data-use" className="hover:text-[#BFFF00]">Data Use Policy</Link>
            <Link to="/cookies" className="hover:text-[#BFFF00]">Cookie Policy</Link>
            <Link to="/security" className="hover:text-[#BFFF00]">Security</Link>
            <Link to="/faq" className="hover:text-[#BFFF00]">FAQ</Link>
          </div>
          <p className="text-center text-gray-500 text-sm mt-6">
            © {new Date().getFullYear()} Revurge Corporation. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
}
